import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { UpdateBankFormData, updateBankSchema } from '../../../services/properties/schema/UpdateBankSchema';
import useShowBank from '../../../services/properties/hooks/useShowBank';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import FormError from '../../../shared/components/errors/FormError';
import { useUpdateBank } from '../../../services/properties/hooks/useUpdateBank';

interface UpdateBankAccountDialogProps {
    show: boolean;
    onClose: () => void;
    bankId: string;
}

const UpdateBankAccountDialog: React.FC<UpdateBankAccountDialogProps> = ({ show, onClose, bankId }) => {
    const { data: bankAccount, isFetching, isError, isSuccess } = useShowBank({
        id: bankId
    });

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<UpdateBankFormData>({
        resolver: zodResolver(updateBankSchema)
    });

    useEffect(() => {
        if (bankAccount) {
            reset({
                account_name: bankAccount.data.account_name,
                account_number: bankAccount.data.account_number,
                bank_name: bankAccount.data.bank_name,
                property_id: bankAccount.data.property_id
            });
        }
    }, [bankAccount]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const { mutate, isPending } = useUpdateBank();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateBankFormData) => {
        toast.loading('Updating bank account...', { id: 'update-bank-account' });

        mutate({
            bankId: bankId,
            formData: data
        }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['banks'].includes(key as string))
                });

                toast.success('Bank account updated successfully', { id: 'update-bank-account' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateBankFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update bank account', { id: 'update-bank-account' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to update bank account', { id: 'update-bank-account' });
                }
            }
        });
    }, [mutate]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            className="custom-z-modal"
            dialogClassName="custom-z-dialog"
        >
            <Modal.Header closeButton>
                <Modal.Title>Update Bank Account</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isError && (<FormError />)}
                {isFetching && (
                    <div className="text-center">
                        <FormSpinner />
                    </div>
                )}
                {isSuccess && !isFetching && bankAccount && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <input type="hidden" {...register('property_id')} />
                        <div className="mb-3">
                            <label htmlFor="bankName" className="form-label">Account Name</label>
                            <input type="text" className={clsx('form-control', { 'is-invalid': errors.account_name })} id="bankName" {...register('account_name')} />
                            {errors.account_name && <div className="invalid-feedback">{errors.account_name.message}</div>}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="bankName" className="form-label">Account Number</label>
                            <input type="text" className={clsx('form-control', { 'is-invalid': errors.account_number })} id="bankName" {...register('account_number')} />
                            {errors.account_number && <div className="invalid-feedback">{errors.account_number.message}</div>}
                        </div>
                        <div className='mb-3'>
                            <label htmlFor="bankName" className="form-label">Bank Name</label>
                            <input type="text" className={clsx('form-control', { 'is-invalid': errors.bank_name })} id="bankName" {...register('bank_name')} />
                            {errors.bank_name && <div className="invalid-feedback">{errors.bank_name.message}</div>}
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={handleClose}>Close</button>
                <button type='submit' className="btn btn-primary" onClick={handleSubmit(onSubmit)} disabled={isPending || isFetching}>Update</button>
            </Modal.Footer>
        </Modal>
    )
}

export default UpdateBankAccountDialog